import { isServer } from '@/utils/helpers';

const getBooleanEnv = (env?: string) => {
  const truthyValues = ['true', '1', 'yes', 'ok'];
  if (env) return truthyValues.includes(env.toLowerCase());
  return false;
};

const getEnvValue = <T extends string | number | boolean>(
  env: string | boolean | undefined,
  defaultValue: T,
): T => {
  try {
    if (env === undefined) return defaultValue;

    if (typeof defaultValue === typeof env) return env as T;

    if (!Number.isNaN(Number(env))) return Number(env) as T;

    return env as T;
  } catch {
    return defaultValue;
  }
};

export interface IClientConfig {
  appSignalKey: string;
  isProduction: boolean;
  maptilerKey: string;
  maptilerUrl: string;
  googleTagManagerId: string;
  facebookPixelId: string;
  linkedinPartnerId: string;
  zmyleWidgetUrl: string;
  enableRecaptcha: boolean;
  recaptchaSiteKey: string;
}

export class ClientConfig {
  private static instance: ClientConfig;

  private static hasBeenSet = false;

  private config: IClientConfig;

  private constructor(config?: Partial<IClientConfig>) {
    const isProduction = process.env.NODE_ENV === 'production';

    const maptilerKey = getEnvValue(process.env.ZMYLE_WWW_UI_MAPTILER_KEY, '');

    this.config = {
      appSignalKey: getEnvValue(process.env.ZMYLE_WWW_UI_APPSIGNAL_KEY, ''),
      isProduction,
      maptilerKey,
      maptilerUrl: `https://api.maptiler.com/maps/db5d3dde-1d27-46eb-a792-db9aa4f4029e/style.json?key=${maptilerKey}`,
      googleTagManagerId: getEnvValue(
        process.env.ZMYLE_WWW_UI_GOOGLE_TAGMANAGER_ID,
        '',
      ),
      facebookPixelId: getEnvValue(
        process.env.ZMYLE_WWW_UI_FACEBOOK_PIXEL_ID,
        '',
      ),
      linkedinPartnerId: getEnvValue(
        process.env.ZMYLE_WWW_UI_LINKEDIN_PARTNER_ID,
        '',
      ),
      zmyleWidgetUrl: isProduction
        ? 'https://edge.zmyle.de/static/zmyle-cookie-widget/zmyle-cookie-widget.min.js'
        : 'https://edge.zmyle.de/static/zmyle-cookie-widget/zmyle-cookie-widget-dev.min.js',
      enableRecaptcha: getBooleanEnv(
        process.env.ZMYLE_WWW_UI_RECAPTCHA_ENABLED,
      ),
      recaptchaSiteKey: getEnvValue(
        process.env.ZMYLE_WWW_UI_RECAPTCHA_SITE_KEY,
        '',
      ),
      ...config,
    };

    if (!isServer()) {
      window.analytics = {};

      if (this.config.facebookPixelId) {
        window.analytics.facebookPixelId = this.config.facebookPixelId;
      }

      if (this.config.linkedinPartnerId) {
        window.analytics.linkedinPartnerId = this.config.linkedinPartnerId;
      }
    }
  }

  public static getInstance(): ClientConfig {
    if (!ClientConfig.instance) ClientConfig.instance = new ClientConfig();

    return ClientConfig.instance;
  }

  public static set(config: Partial<IClientConfig>): void {
    ClientConfig.instance = new ClientConfig(config);

    ClientConfig.hasBeenSet = true;
  }

  public static isPopulated(): boolean {
    if (isServer()) return true;

    return ClientConfig.hasBeenSet;
  }

  public getConfig(): IClientConfig {
    return this.config;
  }
}
